/**
 * useAppCatalog — the public app catalog as a RESOURCE (APP-ENTITLEMENT-001 Phase 11; RES-PRIM-001): `status` is
 * `loading` → `ready` | `error`, a failed read answers a `retry`, and it never folds into "no apps" — a pricing page
 * that cannot read its prices says so and offers to try again; it never renders an empty price list as if nothing
 * were for sale.
 *
 * `@puffin-core/resource-loading` is the ONE loader (its own package, a leaf of React alone), reached directly and
 * never through the page-shell barrel: this hook runs on nine PRERENDERED public sites.
 *
 * @module @puffin-core/marketing-shell/pricing/useAppCatalog
 */

import { useResource } from '@puffin-core/resource-loading';

/**
 * @param {{readCatalog: (opts: {signal: AbortSignal}) => Promise<Object>}} client - `createAppCatalogClient`'s.
 * @returns {{status: string, data: (Object|undefined), error: *, retry: () => Promise<void>}}
 */
export function useAppCatalog(client) {
  const resource = useResource(({ signal }) => client.readCatalog({ signal }), [client]);
  return {
    status: resource.status,
    data: resource.status === 'ready' ? resource.data : undefined,
    error: resource.error,
    retry: resource.refetch,
  };
}

export default useAppCatalog;
